<ng-container *ngIf="config; else loadingTemplate">
  <div class="config-form">
    <!-- Mode Selector -->
    <ng-container *ngIf="config.modeSelector && isModeSelectorVisible()">
      <ng-container *ngIf="config.modeSelector as modeSelector">
        <div class="mode-selector section-card">
          <div class="section-header">
            <span class="section-label body-3 text-grey-400 text-400 text-uppercase">
              {{ modeSelector.label | translate }}
            </span>
          </div>
          <div class="section-content">
            <mat-form-field class="w-full custom-mat-form-field custom-border-bottom mode-select">
              <mat-select
                [disabled]="isBusy"
                [ngModel]="currentMode"
                (ngModelChange)="onModeChange($event)">
                <mat-option
                  *ngFor="let option of modeSelector.options"
                  [value]="option.value">
                  {{ option.label | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <!-- Apply button: only shown in manual mode -->
            <ng-container *ngIf="modeSelector.applyMode !== 'immediate' && currentMode !== originalValues[modeSelector.key]">
              <button
                class="custom-mat-button-primary"
                mat-button
                [disabled]="isSaving"
                (click)="saveModeSelector()">
                {{ isSaving ? _labels.saving : _labels.apply }}
              </button>
            </ng-container>

            <!-- Immediate mode: saving indicator -->
            <span
              *ngIf="modeSelector.applyMode === 'immediate' && isModeApplying"
              class="mode-apply-spinner">
              {{ _labels.loading }}
            </span>
          </div>

          <!-- Mode Selector Error -->
          <div
            *ngIf="lastSaveError && lastSaveError.type === 'save-mode'"
            class="section-error"
            (click)="clearSaveError()">
            <span class="error-icon">⚠</span>
            <span class="error-message">{{ _labels.saveFailed }}</span>
            <span class="error-dismiss">✕</span>
          </div>

          <!-- Global Read: visible when no section has its own Read button -->
          <div
            *ngIf="!hasVisibleSections()"
            class="section-actions">
            <button
              class="custom-mat-button-secondary"
              mat-button
              type="button"
              [disabled]="isSaving || isReading"
              (click)="requestRead()">
              {{ isReading ? _labels.reading : _labels.read }}
            </button>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- Presets -->
    <ng-container *ngIf="getAvailablePresets() as presets">
      <div
        *ngIf="presets.length > 0"
        class="presets-bar">
        <button
          *ngFor="let preset of presets"
          class="preset-card"
          type="button"
          [class.preset-applied]="appliedPresetId === preset.id"
          (click)="applyPreset(preset)">
          <mat-icon
            *ngIf="preset.icon"
            class="preset-icon">
            {{ preset.matIcon || preset.icon }}
          </mat-icon>
          <div class="preset-text">
            <span class="preset-label">{{ preset.label | translate }}</span>
            <span
              *ngIf="preset.description"
              class="preset-description">
              {{ preset.description | translate }}
            </span>
          </div>
          <mat-icon
            *ngIf="appliedPresetId === preset.id"
            class="preset-check">
            check_circle
          </mat-icon>
        </button>
      </div>
    </ng-container>

    <!-- Unsupported notice (all sections hidden by validity mask) -->
    <section *ngIf="!hasVisibleContent" class="border-1 fx-row fx-align-content-start fx-gap-3 p-1 radius-4 border-accent bg-accent-100 w-full">
      <mat-icon class="text-accent" style="font-size: 24px; width: 24px; height: 24px; flex-shrink: 0">info_outline</mat-icon>
      <p class="fx-g-1 tw-text-sm tw-font-normal text-normal-wrap" style="color: var(--accent)">{{ _labels.unsupported }}</p>
    </section>

    <!-- Sections -->
    <ng-container *ngFor="let section of config.sections">
      <div
        *ngIf="isSectionVisible(section)"
        class="section-card"
        [class.dirty]="isSectionDirty(section)"
        [class.section-blocked]="isSectionBusy(section)">
        <div
          *ngIf="!isSingleSection"
          class="section-header">
          <span class="section-label body-3 text-grey-300 text-400 text-uppercase">
            {{ section.label | translate }}
          </span>
          <span
            *ngIf="isSectionDirty(section)"
            class="dirty-indicator">
            *
          </span>
        </div>

        <!-- Section Selector -->
        <ng-container *ngIf="section.sectionSelector as sel">
          <div class="section-selector">
            <label class="field-label body-3 text-grey-400 text-400">{{ sel.label | translate }}</label>
            <div class="section-selector-control">
              <mat-form-field class="w-full custom-mat-form-field custom-border-bottom">
                <mat-select
                  [disabled]="isSectionBusy(section)"
                  [ngModel]="formValues[sel.key]"
                  (ngModelChange)="onSectionSelectorChange(section, $event)">
                  <mat-option
                    *ngFor="let opt of sel.options"
                    [value]="opt.value">
                    {{ opt.label | translate:{ value: opt.value } }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <span
                *ngIf="sel.applyMode === 'immediate' && isSectionApplying(section)"
                class="section-apply-spinner">
                {{ _labels.loading }}
              </span>
            </div>
          </div>
        </ng-container>

        <!-- Custom Section Content -->
        <ng-container *ngIf="section.customContent && customSectionTemplates?.[section.customContent]; else standardSectionContent">
          <ng-container
            *ngTemplateOutlet="$any(customSectionTemplates)[section.customContent];
              context: { $implicit: getCustomSectionContext(section) }">
          </ng-container>
        </ng-container>

        <ng-template #standardSectionContent>
          <div class="section-fields">
            <ng-container *ngFor="let field of section.fields">

              <!-- ═══ Bit Toggle: rendered as sub-group header ═══ -->
              <ng-container *ngIf="field.type === 'bit-toggle' && isFieldVisible(field)">
                <div
                  class="bit-toggle-header"
                  [class.bit-toggle-on]="!!formValues[field.key]"
                  [class.bit-toggle-off]="!formValues[field.key]">
                  <div class="bit-toggle-label">
                    <span class="body-3 text-400">{{ field.label | translate }}</span>
                    <span class="bit-toggle-badge" [class.badge-on]="!!formValues[field.key]">
                      {{ (formValues[field.key] ? 'i18n.Common.enabled' : 'i18n.Common.disabled') | translate }}
                    </span>
                  </div>
                  <mat-checkbox
                    color="accent"
                    [checked]="!!formValues[field.key]"
                    (change)="onFieldChange(field, $event.checked)">
                  </mat-checkbox>
                </div>
              </ng-container>

              <!-- ═══ Bit Select: rendered as sub-group header ═══ -->
              <ng-container *ngIf="field.type === 'bit-select' && isFieldVisible(field)">
                <div class="bit-select-header">
                  <label class="field-label body-3 text-grey-400 text-400">
                    {{ field.label | translate }}
                  </label>
                  <div class="field-input">
                    <mat-form-field class="custom-mat-form-field custom-border-bottom w-full">
                      <mat-select
                        [ngModel]="formValues[field.key]"
                        (ngModelChange)="onFieldChange(field, $event)">
                        <mat-option
                          *ngFor="let opt of getFieldOptions(field)"
                          [value]="opt.value">
                          {{ opt.label | translate }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>
                </div>
              </ng-container>

              <!-- ═══ Checkbox (binary enable/disable) ═══ -->
              <ng-container *ngIf="field.type === 'checkbox' && isFieldVisible(field)">
                <div
                  class="bit-toggle-header"
                  [class.bit-toggle-on]="!!formValues[field.key]"
                  [class.bit-toggle-off]="!formValues[field.key]"
                  [class.dependent-field]="field.visibleWhen">
                  <div class="bit-toggle-label">
                    <span class="body-3 text-400">{{ field.label | translate }}</span>
                    <span class="bit-toggle-badge" [class.badge-on]="!!formValues[field.key]">
                      {{ (formValues[field.key] ? 'i18n.Common.enabled' : 'i18n.Common.disabled') | translate }}
                    </span>
                  </div>
                  <mat-checkbox
                    color="accent"
                    [checked]="!!formValues[field.key]"
                    (change)="onFieldChange(field, $event.checked ? 1 : 0)">
                  </mat-checkbox>
                </div>
              </ng-container>

              <!-- ═══ Standard fields (non-bit) ═══ -->
              <ng-container *ngIf="field.type !== 'bit-toggle' && field.type !== 'bit-select' && field.type !== 'checkbox'">
              <div
                *ngIf="isFieldVisible(field)"
                class="field-row"
                [class.has-error]="errors[field.key]"
                [class.dependent-field]="field.visibleWhen">
                <label
                  *ngIf="field.type !== 'trigger' || !field.constraint?.label"
                  class="field-label body-3 text-grey-400 text-400">
                  {{ field.label | translate }}
                </label>

                <div
                  class="field-input"
                  [class.composite]="field.type === 'time' || field.type === 'date' || field.type === 'weekday'"
                  [class.trigger-field]="field.type === 'trigger'">
                  <ng-container [ngSwitch]="field.type">
                    <!-- Input -->
                    <ng-container *ngSwitchCase="'input'">
                      <mat-form-field class="custom-mat-form-field custom-border-bottom w-full">
                        <input
                          matInput
                          [max]="field.constraint?.max"
                          [min]="field.constraint?.min"
                          [ngModel]="formValues[field.key]"
                          [placeholder]="field.label"
                          [step]="field.constraint?.step || 1"
                          [type]="field.inputType || 'number'"
                          (ngModelChange)="onFieldChange(field, $event)">
                      </mat-form-field>
                    </ng-container>

                    <!-- Select -->
                    <ng-container *ngSwitchCase="'select'">
                      <mat-form-field class="custom-mat-form-field custom-border-bottom w-full">
                        <mat-select
                          [ngModel]="formValues[field.key]"
                          (ngModelChange)="onFieldChange(field, $event)">
                          <mat-option
                            *ngFor="let opt of getFieldOptions(field); trackBy: trackByOptionValue"
                            [value]="opt.value">
                            {{ opt.label | translate }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field>
                    </ng-container>

                    <!-- Time (HH:MM) -->
                    <ng-container *ngSwitchCase="'time'">
                      <div class="date-select-group">
                        <mat-form-field class="date-select hour-select custom-mat-form-field">
                          <mat-select
                            [ngModel]="getTimeHour(field)"
                            (ngModelChange)="setTimeHour(field, $event)">
                            <mat-option
                              *ngFor="let h of hours"
                              [value]="h">
                              {{ h < 10 ? '0' + h : h }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <span class="date-separator">:</span>
                        <mat-form-field class="date-select minute-select custom-mat-form-field">
                          <mat-select
                            [ngModel]="getTimeMinute(field)"
                            (ngModelChange)="setTimeMinute(field, $event)">
                            <mat-option
                              *ngFor="let m of minutes"
                              [value]="m">
                              {{ m < 10 ? '0' + m : m }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>
                    </ng-container>

                    <!-- Date (MM/DD) -->
                    <ng-container *ngSwitchCase="'date'">
                      <div class="date-select-group">
                        <mat-form-field class="date-select month-select custom-mat-form-field">
                          <mat-select
                            [ngModel]="getDateMonth(field)"
                            (ngModelChange)="setDateMonth(field, $event)">
                            <mat-option
                              *ngFor="let m of months"
                              [value]="m.value">
                              {{ m.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <span class="date-separator">/</span>
                        <mat-form-field class="date-select day-select custom-mat-form-field">
                          <mat-select
                            [ngModel]="getDateDay(field)"
                            (ngModelChange)="setDateDay(field, $event)">
                            <mat-option
                              *ngFor="let d of days"
                              [value]="d">
                              {{ d }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>
                    </ng-container>

                    <!-- Weekday -->
                    <ng-container *ngSwitchCase="'weekday'">
                      <div class="weekday-selector">
                        <label
                          *ngFor="let day of weekdays"
                          class="weekday-option"
                          [class.selected]="getWeekdayValue(field).includes(day)">
                          <input
                            type="checkbox"
                            [checked]="getWeekdayValue(field).includes(day)"
                            (change)="toggleWeekday(field, day, $any($event).target.checked)">
                          <span>{{ day }}</span>
                        </label>
                      </div>
                    </ng-container>

                    <!-- Trigger -->
                    <ng-container *ngSwitchCase="'trigger'">
                      <div class="trigger-action">
                        <span
                          *ngIf="field.constraint?.label"
                          class="trigger-description body-3 text-grey-400">
                          {{ field.constraint!.label! | translate }}
                        </span>
                        <button
                          class="custom-mat-button-primary"
                          mat-button
                          [disabled]="isSaving"
                          (click)="triggerAction(field, section)">
                          {{ _labels.trigger }}
                        </button>
                      </div>
                    </ng-container>
                  </ng-container>

                  <span
                    *ngIf="field.unit"
                    class="field-unit">
                    {{ field.unit }}
                  </span>
                </div>

                <div
                  *ngIf="errors[field.key]"
                  class="field-error">
                  {{ errors[field.key] }}
                </div>
              </div>
              </ng-container>
            </ng-container>
          </div>

          <!-- Section Error -->
          <div
            *ngIf="lastSaveError && lastSaveError.sectionId === section.id"
            class="section-error"
            (click)="clearSaveError()">
            <span class="error-icon">⚠</span>
            <span class="error-message">{{ _labels.saveFailed }}</span>
            <span class="error-dismiss">✕</span>
          </div>

          <!-- Section Actions -->
          <div class="section-actions">
            <button
              class="custom-mat-button-secondary"
              mat-button
              type="button"
              [disabled]="isSaving || isReading"
              (click)="requestRead()">
              {{ isReading ? _labels.reading : _labels.read }}
            </button>
            <div class="section-actions-end">
              <button
                mat-button
                type="button"
                [disabled]="!isSectionDirty(section) || isSaving || isReading"
                (click)="resetSection(section)">
                {{ _labels.discard }}
              </button>
              <button
                class="custom-mat-button-primary"
                mat-button
                type="button"
                [disabled]="!isSectionDirty(section) || !isValid || isSaving || isReading"
                (click)="saveSection(section)">
                {{ isSaving ? _labels.saving : (willSaveModeWithSection(section) ? _labels.saveAll : _labels.save) }}
              </button>
            </div>
          </div>
        </ng-template>
      </div>
    </ng-container>
  </div>
</ng-container>

<ng-template #loadingTemplate>
  <div class="loading">
    <span>{{ _labels.loading }}</span>
  </div>
</ng-template>
